import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiCheck, FiEdit2, FiMapPin, FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GolfClubAutocomplete from './GolfClubAutocomplete';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  getUserProfile,
  setUserCountry,
  setUserHomeCourse,
  setUserTimezone,
} from '../services/userProfileService';

// Hard-coded ISO-3166-1 alpha-2 list as a fallback when the runtime lacks
// Intl.supportedValuesOf (older WebViews). Kept minimal - just codes; names
// come from Intl.DisplayNames.
const ISO_3166_ALPHA2_FALLBACK = [
  'AD','AE','AF','AG','AI','AL','AM','AO','AQ','AR','AS','AT','AU','AW','AX','AZ',
  'BA','BB','BD','BE','BF','BG','BH','BI','BJ','BL','BM','BN','BO','BQ','BR','BS',
  'BT','BV','BW','BY','BZ','CA','CC','CD','CF','CG','CH','CI','CK','CL','CM','CN',
  'CO','CR','CU','CV','CW','CX','CY','CZ','DE','DJ','DK','DM','DO','DZ','EC','EE',
  'EG','EH','ER','ES','ET','FI','FJ','FK','FM','FO','FR','GA','GB','GD','GE','GF',
  'GG','GH','GI','GL','GM','GN','GP','GQ','GR','GS','GT','GU','GW','GY','HK','HM',
  'HN','HR','HT','HU','ID','IE','IL','IM','IN','IO','IQ','IR','IS','IT','JE','JM',
  'JO','JP','KE','KG','KH','KI','KM','KN','KP','KR','KW','KY','KZ','LA','LB','LC',
  'LI','LK','LR','LS','LT','LU','LV','LY','MA','MC','MD','ME','MF','MG','MH','MK',
  'ML','MM','MN','MO','MP','MQ','MR','MS','MT','MU','MV','MW','MX','MY','MZ','NA',
  'NC','NE','NF','NG','NI','NL','NO','NP','NR','NU','NZ','OM','PA','PE','PF','PG',
  'PH','PK','PL','PM','PN','PR','PS','PT','PW','PY','QA','RE','RO','RS','RU','RW',
  'SA','SB','SC','SD','SE','SG','SH','SI','SJ','SK','SL','SM','SN','SO','SR','SS',
  'ST','SV','SX','SY','SZ','TC','TD','TF','TG','TH','TJ','TK','TL','TM','TN','TO',
  'TR','TT','TV','TW','TZ','UA','UG','UM','US','UY','UZ','VA','VC','VE','VG','VI',
  'VN','VU','WF','WS','YE','YT','ZA','ZM','ZW',
];

let _countryOptionsCache = null;

// Returns the full ISO-3166-1 alpha-2 list as { code, name } pairs sorted
// alphabetically by name (en-GB). Memoised - derived once per page load.
const getCountryOptions = () => {
  if (_countryOptionsCache) return _countryOptionsCache;

  let codes;
  try {
    if (typeof Intl?.supportedValuesOf === 'function') {
      codes = Intl.supportedValuesOf('region').filter((c) => /^[A-Z]{2}$/.test(c));
    }
  } catch {
    // Fall through to fallback.
  }
  if (!codes || codes.length === 0) codes = ISO_3166_ALPHA2_FALLBACK;

  let displayNames;
  try {
    displayNames = new Intl.DisplayNames(['en-GB'], { type: 'region' });
  } catch {
    displayNames = null;
  }

  const options = codes
    .map((code) => ({ code, name: displayNames?.of(code) || code }))
    .sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));

  _countryOptionsCache = options;
  return options;
};

// Common IANA timezones. Same pattern - covers the most-used zones for
// TeeSync's audience; user can also paste/select any other IANA name via
// the browser's full Intl.supportedValuesOf('timeZone') list.
const TIMEZONE_OPTIONS = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Lisbon',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
  'Africa/Johannesburg',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Dubai',
];

const getAllTimezones = () => {
  try {
    if (typeof Intl?.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch {
    // Fall through.
  }
  return TIMEZONE_OPTIONS;
};

let _countryDisplayNames = null;
const getCountryName = (code) => {
  if (!code) return '';
  if (!_countryDisplayNames) {
    try {
      _countryDisplayNames = new Intl.DisplayNames(['en-GB'], { type: 'region' });
    } catch {
      _countryDisplayNames = null;
    }
  }
  return _countryDisplayNames?.of(code) || code;
};

const formatCountry = (code) => {
  if (!code) return '';
  const name = getCountryName(code);
  return name && name !== code ? `${name} (${code})` : code;
};

function CountryAutocomplete({ value, onChange, disabled }) {
  const options = useMemo(getCountryOptions, []);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Sync the input display when the selected value changes externally and
  // the dropdown is closed. Keeps the typed query intact while editing.
  useEffect(() => {
    if (!open) setQuery(value ? formatCountry(value) : '');
  }, [value, open]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return undefined;
    const onDocPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocPointerDown);
    return () => document.removeEventListener('mousedown', onDocPointerDown);
  }, [open]);

  // When the user opens the picker, the input shows the current selection
  // verbatim - treat that as "no filter" so they see the full list to scroll.
  const currentLabel = value ? formatCountry(value) : '';
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || query === currentLabel) return options.slice(0, 50);
    const prefix = [];
    const substring = [];
    for (const opt of options) {
      const name = opt.name.toLowerCase();
      const code = opt.code.toLowerCase();
      if (name.startsWith(q) || code === q) prefix.push(opt);
      else if (name.includes(q) || code.startsWith(q)) substring.push(opt);
    }
    return [...prefix, ...substring].slice(0, 50);
  }, [query, options, currentLabel]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Keep the active option visible when navigating with the keyboard.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[activeIndex];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, open]);

  const select = (option) => {
    onChange(option.code);
    setQuery(`${option.name} (${option.code})`);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[activeIndex]) {
        e.preventDefault();
        select(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const onClear = () => {
    onChange('');
    setQuery('');
    setOpen(true);
    inputRef.current?.focus();
  };

  return (
    <div className="relative min-w-0 flex-1 max-w-xs" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={(e) => {
          setOpen(true);
          // Select-all so a single keystroke replaces the current selection.
          if (value) e.target.select();
        }}
        onKeyDown={onKeyDown}
        disabled={disabled}
        placeholder="Search country…"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        className="w-full px-3 py-2 pr-8 bg-surface border border-border-subtle rounded-lg text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors disabled:opacity-50"
      />
      {(query || value) && !disabled && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear country"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary p-1 rounded transition-colors"
        >
          <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
        </button>
      )}
      <AnimatePresence>
        {open && filtered.length > 0 && (
          <motion.ul
            ref={listRef}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            role="listbox"
            className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-card border border-border-subtle rounded-lg shadow-card"
          >
            {filtered.map((option, idx) => (
              <li
                key={option.code}
                role="option"
                aria-selected={idx === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(option);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between gap-2 ${
                  idx === activeIndex ? 'bg-surface' : 'hover:bg-surface'
                }`}
              >
                <span className="text-text-primary truncate">{option.name}</span>
                <span className="text-text-muted text-xs flex-shrink-0">{option.code}</span>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

function LocationSection() {
  const { user } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  // Inline edit state per field.
  const [editingField, setEditingField] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [draftCourse, setDraftCourse] = useState(null);
  const [draftCountry, setDraftCountry] = useState('');
  const [draftTimezone, setDraftTimezone] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await getUserProfile(user);
      if (!cancelled) {
        setProfile(data);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const allTimezones = useMemo(getAllTimezones, []);

  const homeCourseLabel = profile?.home_course_name || '';
  const countryLabel = profile?.country_code ? formatCountry(profile.country_code) : '';
  const timezoneLabel = profile?.timezone || '';

  const startEdit = (field) => {
    setEditingField(field);
    if (field === 'home_course') {
      setDraftCourse(
        profile?.home_course_name
          ? { id: profile.home_course_id, name: profile.home_course_name }
          : null,
      );
    } else if (field === 'country') {
      setDraftCountry(profile?.country_code || '');
    } else if (field === 'timezone') {
      setDraftTimezone(profile?.timezone || '');
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setDraftCourse(null);
    setDraftCountry('');
    setDraftTimezone('');
  };

  const saveHomeCourse = async () => {
    if (!draftCourse || draftCourse.latitude == null || draftCourse.longitude == null) {
      toast.error('Pick a course from the suggestions so we can set your timezone.');
      return;
    }
    setSavingField('home_course');
    const { data, error } = await setUserHomeCourse(draftCourse, user);
    setSavingField(null);
    if (error) {
      toast.error("Couldn't save your home course.");
      return;
    }
    setProfile(data);
    setEditingField(null);
    setDraftCourse(null);
    toast.success('Home course updated.');
  };

  const saveCountry = async () => {
    setSavingField('country');
    const { data, error } = await setUserCountry(draftCountry, user);
    setSavingField(null);
    if (error) {
      toast.error("Couldn't save your country.");
      return;
    }
    setProfile(data);
    setEditingField(null);
    toast.success('Country updated.');
  };

  const saveTimezone = async () => {
    setSavingField('timezone');
    const { data, error } = await setUserTimezone(draftTimezone, user);
    setSavingField(null);
    if (error) {
      toast.error("Couldn't save your timezone.");
      return;
    }
    setProfile(data);
    setEditingField(null);
    toast.success('Timezone updated.');
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <SafeIcon icon={FiMapPin} className="w-5 h-5 text-text-secondary" />
        <h3 className="text-lg font-bold text-text-primary">Location</h3>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-text-secondary text-sm">Loading</span>
        </div>
      ) : (
        <div className="divide-y divide-border-subtle">
          {/* Home course */}
          <div className="py-3">
            {editingField === 'home_course' ? (
              <div className="space-y-3">
                <span className="text-sm font-medium text-text-secondary">Home course</span>
                <GolfClubAutocomplete
                  selectedCourse={draftCourse}
                  onCourseSelect={setDraftCourse}
                  placeholder="Search for a golf course…"
                  className="w-full"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={cancelEdit}
                    disabled={savingField === 'home_course'}
                    className="px-3 py-2 text-sm min-h-[36px] border border-border-subtle text-text-secondary rounded-lg hover:bg-surface transition-colors flex items-center justify-center disabled:opacity-50"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={saveHomeCourse}
                    disabled={savingField === 'home_course'}
                    className="px-3 py-2 text-sm min-h-[36px] bg-accent text-white rounded-lg hover:bg-interactive-hover transition-colors flex items-center justify-center disabled:opacity-50"
                  >
                    {savingField === 'home_course' ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-text-muted">
                  Changing your home course also updates the country and timezone below.
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-text-secondary flex-shrink-0">Home course</span>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-text-primary truncate">
                    {homeCourseLabel || <span className="text-text-muted">Not set</span>}
                  </span>
                  <button
                    onClick={() => startEdit('home_course')}
                    aria-label="Edit home course"
                    className="text-text-muted hover:text-text-primary p-1.5 -mr-1.5 rounded transition-colors"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Country */}
          <div className="py-3">
            {editingField === 'country' ? (
              <div className="flex items-center justify-between py-1 gap-3">
                <span className="text-sm font-medium text-text-secondary flex-shrink-0">Country</span>
                <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                  <CountryAutocomplete
                    value={draftCountry}
                    onChange={setDraftCountry}
                    disabled={savingField === 'country'}
                  />
                  <button
                    onClick={saveCountry}
                    disabled={savingField === 'country'}
                    className="px-3 py-2 text-sm min-h-[36px] bg-accent text-white rounded-lg hover:bg-interactive-hover transition-colors flex items-center justify-center disabled:opacity-50"
                  >
                    {savingField === 'country' ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={cancelEdit}
                    disabled={savingField === 'country'}
                    className="px-3 py-2 text-sm min-h-[36px] border border-border-subtle text-text-secondary rounded-lg hover:bg-surface transition-colors flex items-center justify-center disabled:opacity-50"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-text-secondary flex-shrink-0">Country</span>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-text-primary truncate">
                    {countryLabel || <span className="text-text-muted">Not set</span>}
                  </span>
                  <button
                    onClick={() => startEdit('country')}
                    aria-label="Edit country"
                    className="text-text-muted hover:text-text-primary p-1.5 -mr-1.5 rounded transition-colors"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Timezone */}
          <div className="py-3">
            {editingField === 'timezone' ? (
              <div className="flex items-center justify-between py-1 gap-3">
                <span className="text-sm font-medium text-text-secondary flex-shrink-0">Timezone</span>
                <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                  <select
                    value={draftTimezone}
                    onChange={(e) => setDraftTimezone(e.target.value)}
                    disabled={savingField === 'timezone'}
                    className="min-w-0 flex-1 max-w-xs px-3 py-2 bg-surface border border-border-subtle rounded-lg text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
                  >
                    <option value="">Not set</option>
                    {allTimezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={saveTimezone}
                    disabled={savingField === 'timezone'}
                    className="px-3 py-2 text-sm min-h-[36px] bg-accent text-white rounded-lg hover:bg-interactive-hover transition-colors flex items-center justify-center disabled:opacity-50"
                  >
                    {savingField === 'timezone' ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={cancelEdit}
                    disabled={savingField === 'timezone'}
                    className="px-3 py-2 text-sm min-h-[36px] border border-border-subtle text-text-secondary rounded-lg hover:bg-surface transition-colors flex items-center justify-center disabled:opacity-50"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-text-secondary flex-shrink-0">Timezone</span>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-text-primary truncate">
                    {timezoneLabel || <span className="text-text-muted">Not set</span>}
                  </span>
                  <button
                    onClick={() => startEdit('timezone')}
                    aria-label="Edit timezone"
                    className="text-text-muted hover:text-text-primary p-1.5 -mr-1.5 rounded transition-colors"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationSection;
